import { BorderColor, MinimumSlideInformation, NodeType, SlideType } from "@pcp/core/interfaces";
import { isCustomerModuleSlide, isDynamicExtensionSlideType, replaceSignatureUrls } from "@pcp/core/util";

/**
 * List of MinimumSlideInformation optimized for reduced network payload.
 *
 * If the list contains the lection/chapter's basic slides,
 * it additionally includes the it information if there's a parent slide (parent lection or chapter)
 * -> used to enable navigation to one level up.
 */
export interface ITransferMinimumSlideInformationList {
    /** BaseUrl to the blob storage used for all images */
    imageBaseUrl: string;

    /** Access signature used for all images */
    sas: string;

    slides: ITransferMinimumSlideInformation[];

    hasParentSlide?: boolean;
}

export interface ITransferMinimumSlideInformation {
    id: number;
    caption?: string;
    parentId: number;
    type: SlideType;
    nodeType: NodeType;
    bordercolor: BorderColor;

    /** normal thumbnail URL (part) */
    thumb: string;
    showCaption: boolean;
}

export const mapToITransferMinimumSlideInformationList = (
    slides: MinimumSlideInformation[],
    baseUrl: string,
    sas: string,
    hasParentSlide?: boolean
): ITransferMinimumSlideInformationList => ({
    imageBaseUrl: baseUrl,
    sas,
    slides: slides.map(s => mapToITransferMinimumSlideInformation(s, baseUrl)),
    hasParentSlide
});

const mapToITransferMinimumSlideInformation = (slide: MinimumSlideInformation, baseUrl: string): ITransferMinimumSlideInformation => ({
    id: slide.id,
    // Slide caption is required for Text extension slides and difficulty questions.
    // Remove the caption for all other slide types to save payload.
    caption: isDynamicExtensionSlideType(slide.type)
    || isCustomerModuleSlide(slide.id) && (slide.showCaption || [NodeType.lection, NodeType.chapter].indexOf(slide.nodeType) > -1)
    || slide.type === SlideType.difficultQuestion ?
        slide.caption : undefined,
    parentId: slide.parentId,
    type: slide.type,
    nodeType: slide.nodeType,
    bordercolor: slide.bordercolor,
    thumb: slide.previewpicture.thumb_n.replace(baseUrl, ''),
    showCaption: slide.showCaption,
})

export const reverseMapITransferMinimumSlideInformation = (list: ITransferMinimumSlideInformationList, publicUrl?: string): MinimumSlideInformation[] => list
    .slides
    .map((slide): MinimumSlideInformation => {
        let thumb = slide.thumb;
        if (slide.thumb && slide.type !== SlideType.driverscam) {
            thumb = `${list.imageBaseUrl}${slide.thumb}`;
        }

        return ({
            id: slide.id,
            parentId: slide.parentId,
            type: slide.type,
            nodeType: slide.nodeType,
            bordercolor: slide.bordercolor,
            caption: slide.caption ?? '',
            previewpicture: {
                thumb_n: thumb,
                thumb_h: thumb,
            },
            showCaption: slide.showCaption,
        });
    })
    .map(slide => replaceSignatureUrls(slide, list.sas, publicUrl));
